import { Link, router, usePage } from '@inertiajs/react'
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { SharedProps } from '../types'

// match：高亮判定用的前缀，默认就是 href。/todo 的五个视图各自一个地址
// （/todo/today、/todo/backlog…），href 指向默认视图，高亮要按 /todo 判，
// 否则访问 /todo/backlog 时这一项不会亮。
type NavLeaf = { href: string; label: string; match?: string }
type NavGroup = { label: string; items: NavLeaf[] }
type NavEntry = NavLeaf | NavGroup

const isGroup = (entry: NavEntry): entry is NavGroup => 'items' in entry

// 顶栏只放五个顶级项。原来十个平级项里，四个是「看番茄钟数据」的不同切面
// （仪表盘 / 数据洞察 / 日历 / 历史记录），三个是「组织任务」的不同切面
// （计划 / 项目 / AI Tasker）—— 平铺时它们互相稀释，收进两个下拉之后每一
// 组各自的含义反而更清楚。
//
// 一项都没删：顶栏是这些页面的唯一入口（除 /worklog 那一组页面之间互相有
// 链接外，全前端再没有指向它们的 href），删掉就等于让页面失联。
const NAV_AUTH: NavEntry[] = [
  { href: '/todo/today', label: '待办', match: '/todo' },
  { href: '/', label: '番茄钟' },
  {
    label: '规划',
    items: [
      { href: '/tracker/plan', label: '计划' },
      { href: '/tracker/projects', label: '项目' },
      { href: '/ai-tasker', label: 'AI Tasker' },
    ],
  },
  {
    label: '统计',
    items: [
      { href: '/dashboard', label: '仪表盘' },
      { href: '/insights', label: '数据洞察' },
      { href: '/calendar', label: '日历' },
      { href: '/history', label: '历史记录' },
    ],
  },
  { href: '/worklog', label: 'AI工作日志' },
  { href: '/wiki', label: 'wiki' },
]

// 未登录能看的两页。番茄钟是 SEO 落地页，不能藏进下拉；/developers 是接入
// 文档，公开的意义就在于能分享、能被搜到，藏在登录后两样都做不到。
const NAV_PUBLIC: NavEntry[] = [
  { href: '/', label: '番茄钟' },
  { href: '/developers', label: '接入' },
]

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`ml-1 h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

// 自足的下拉：不引 antd。Header 挂在每个页面上（含 SEO 落地页 /），现在是
// 零 antd 依赖，不值得为一个下拉把整个组件库拖进首屏。
//
// Inertia 的 layout 在同类型页面之间跳转时不会卸载（AppLayout 一直挂着），
// 所以点了里面的链接必须自己收起来，否则跳到新页面下拉还开着。面板里只有
// 链接，所以在面板上兜一个 onClick 就够，不用逐个链接挂。
function NavDropdown({
  label,
  active,
  align = 'left',
  children,
}: {
  label: string
  active: boolean
  align?: 'left' | 'right'
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
          active
            ? 'border-b-2 border-blue-500 text-gray-900'
            : 'border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
        }`}
      >
        {label}
        <Chevron open={open} />
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          className={`absolute top-full z-20 w-40 rounded-md border border-gray-200 bg-white py-1 shadow-lg ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {children}
        </div>
      )}
    </div>
  )
}

export default function Header() {
  const { props, url } = usePage<SharedProps>()
  const user = props.auth?.user
  const isAuthenticated = !!user
  const [mobileOpen, setMobileOpen] = useState(false)

  const path = url.split('?')[0]
  const isActive = (href: string, match?: string) => {
    const prefix = match ?? href
    return prefix === '/' ? path === '/' : path.startsWith(prefix)
  }
  const groupActive = (group: NavGroup) => group.items.some((item) => isActive(item.href, item.match))

  const nav = isAuthenticated ? NAV_AUTH : NAV_PUBLIC
  const closeMobile = () => setMobileOpen(false)
  const logout = () => router.delete('/logout')

  const desktopLink = ({ href, label, match }: NavLeaf) => (
    <Link
      key={href}
      href={href}
      className={`inline-flex items-center px-1 pt-1 text-sm font-medium ${
        isActive(href, match)
          ? 'border-b-2 border-blue-500 text-gray-900'
          : 'border-b-2 border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
      }`}
    >
      {label}
    </Link>
  )

  const menuLink = ({ href, label, match }: NavLeaf) => (
    <Link
      key={href}
      href={href}
      className={`block px-4 py-2 text-sm ${
        isActive(href, match)
          ? 'bg-blue-50 font-medium text-blue-700'
          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
      }`}
    >
      {label}
    </Link>
  )

  // 移动端菜单同样要在跳转后收起 —— layout 不卸载，状态会留着。
  const mobileLink = ({ href, label, match }: NavLeaf, indented = false) => (
    <Link
      key={href}
      href={href}
      onClick={closeMobile}
      className={`block py-2 pr-4 text-base font-medium ${indented ? 'pl-8' : 'pl-3'} ${
        isActive(href, match)
          ? 'border-l-4 border-blue-500 bg-blue-50 text-blue-700'
          : 'border-l-4 border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800'
      }`}
    >
      {label}
    </Link>
  )

  return (
    <header className="bg-white shadow">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 justify-between">
          <div className="flex">
            <div className="flex flex-shrink-0 items-center">
              <Link href="/" className="text-xl font-bold text-blue-600">
                asaptodo
              </Link>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              {nav.map((entry) =>
                isGroup(entry) ? (
                  <NavDropdown key={entry.label} label={entry.label} active={groupActive(entry)}>
                    {entry.items.map(menuLink)}
                  </NavDropdown>
                ) : (
                  desktopLink(entry)
                )
              )}
            </div>
          </div>

          <div className="hidden space-x-4 sm:ml-6 sm:flex sm:items-center">
            {isAuthenticated ? (
              <NavDropdown
                label={user?.name || user?.email || '账户'}
                active={isActive('/settings') || isActive('/developers')}
                align="right"
              >
                {menuLink({ href: '/settings', label: '设置' })}
                {menuLink({ href: '/developers', label: '接入' })}
                {user?.admin && menuLink({ href: '/admin', label: '后台' })}
                <button
                  type="button"
                  onClick={logout}
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                >
                  退出
                </button>
              </NavDropdown>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  href="/login"
                  className="inline-flex items-center rounded-md border border-transparent bg-blue-100 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  登录
                </Link>
                <Link
                  href="/register"
                  className="inline-flex items-center rounded-md border border-transparent bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  注册
                </Link>
              </div>
            )}
          </div>

          <div className="flex items-center sm:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((o) => !o)}
            >
              <span className="sr-only">打开菜单</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={mobileOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* 窄屏放不下下拉，改成分节列表：组名当小标题，组内项缩进一级。 */}
      <div className={`sm:hidden ${mobileOpen ? 'block' : 'hidden'}`}>
        <div className="space-y-1 pb-3 pt-2">
          {nav.map((entry) =>
            isGroup(entry) ? (
              <div key={entry.label}>
                <div className="px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {entry.label}
                </div>
                {entry.items.map((item) => mobileLink(item, true))}
              </div>
            ) : (
              mobileLink(entry)
            )
          )}

          {isAuthenticated ? (
            <div className="border-t border-gray-200 pb-3 pt-4">
              <div className="flex items-center px-4">
                <div className="text-base font-medium text-gray-800">{user?.name || user?.email}</div>
              </div>
              <div className="mt-3 space-y-1">
                {mobileLink({ href: '/settings', label: '设置' })}
                {mobileLink({ href: '/developers', label: '接入' })}
                <button
                  type="button"
                  onClick={logout}
                  className="block w-full px-4 py-2 text-left text-base font-medium text-gray-600 hover:bg-gray-100"
                >
                  退出
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-1 border-t border-gray-200 pb-3 pt-4">
              {mobileLink({ href: '/login', label: '登录' })}
              {mobileLink({ href: '/register', label: '注册' })}
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
